import { useEffect, useState } from "react";
import { useInjection } from "@presentation/components/dependencyInjection";
import { ProjectManagementService } from "@application/useCases/workspace/ProjectManagementService";
import type { SoftwareEnumSchema } from "@domain/schemas/BI";

// `BI.schema.d.ts` enums have no runtime object under Vitest (ambient/type-only in a .d.ts file;
// Vite's esbuild transpiles it anyway, so production is unaffected) - string literals cast to the
// enum type sidestep that, matching this codebase's established workaround.
const SPECTRONAUT = "Spectronaut" as SoftwareEnumSchema;

export interface EffectiveProjectConfiguration {
  allowedSnVersions: string[];
  defaultSnVersion: string | null;
}

const emptyConfiguration: EffectiveProjectConfiguration = { allowedSnVersions: [], defaultSnVersion: null };

/**
 * Resolves a project's effective (Deployment -> Team -> Project fallback-walked) Spectronaut
 * version allow-list/default. Empty/unset until a project is active or the request resolves -
 * every consumer of these fields already treats that the same as "no configuration". Project-level
 * (not team-level) so a project's own override is honored instead of being overridden back by its
 * team's configuration.
 */
export const useEffectiveProjectConfiguration = (projectId: string | null | undefined): EffectiveProjectConfiguration => {
  const projectManagementService = useInjection(ProjectManagementService);
  const [configuration, setConfiguration] = useState<EffectiveProjectConfiguration>(emptyConfiguration);

  useEffect(() => {
    if (!projectId) {
      setConfiguration(emptyConfiguration);
      return;
    }

    let isCancelled = false;

    const loadEffectiveConfiguration = async () => {
      try {
        const softwareVersions = await projectManagementService.getEffectiveSoftwareVersionConfigurationAsync(projectId);
        if (isCancelled) return;

        const spectronautVersions = softwareVersions.allowLists?.[SPECTRONAUT];
        const spectronautDefault = softwareVersions.defaults?.[SPECTRONAUT];

        setConfiguration({
          allowedSnVersions: spectronautVersions?.versions ? [...spectronautVersions.versions] : [],
          defaultSnVersion: spectronautDefault?.version ?? null,
        });
      } catch {
        if (!isCancelled) setConfiguration(emptyConfiguration);
      }
    };

    void loadEffectiveConfiguration();
    return () => {
      isCancelled = true;
    };
  }, [projectId, projectManagementService]);

  return configuration;
};

export default useEffectiveProjectConfiguration;
